import globalWindow from '@utils/globals';
import { withWindow } from '@utils/withWindow';
import { useCallback, useEffect, useRef } from 'react';
import { useIframeMessageBusContext } from './IframeMessageBusProvider';
import { createIframeMessageEventHandler } from './utils';

export const useAttachToIframe = () => {
  const { iframeRef, attachIframeRef, setHasIframeInitialized, hasIframeInitialized } = useIframeMessageBusContext();
  const isAttachingContextRef = useRef(false);
  const iframeEl = iframeRef.current;

  const wrappedAttachIframeRef = useCallback<typeof attachIframeRef>(
    node => {
      isAttachingContextRef.current = true;
      attachIframeRef(node);
    },
    [attachIframeRef]
  );

  useEffect(
    withWindow(
      global => {
        if (!iframeEl) {
          return () => {};
        }

        const receiveMessage = createIframeMessageEventHandler((msg, ev) => {
          if (msg.action === 'previewInitialized' && isAttachingContextRef.current) {
            setHasIframeInitialized(true);
          }
        });
        global.addEventListener('message', receiveMessage, false);
        return () => {
          global.removeEventListener('message', receiveMessage, false);
        };
      },
      () => {}
    ),
    [iframeEl, setHasIframeInitialized]
  );

  // Reset iframe ref if this is the current attaching context
  useEffect(() => {
    return () => {
      if (isAttachingContextRef.current) {
        attachIframeRef(null);
      }
    };
  }, [attachIframeRef]);

  return {
    attachIframeRef: wrappedAttachIframeRef,
    hasIframeInitialized,
    iframeRef,
  };
};
